import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { state } from '../../constants/hc_plandata';
import { useToast } from '../../context/ToastContext';
import { useLoading } from '../../context/LoadingContext';
import './AddEditCourtDetailsForm.scss';
import { getCompanyDataByID } from '../../services/api/CompanyApi';
import { companyaddeditcourtdetails } from '../../services/api/SuperAdminApi';

// Import your district court mapping JSON data
import { districtCourtMapping } from '../../constants/districtCourtMap';

// District court scrape types. Entries saved before bar code support have no 'scrape-type'
// and are treated as 'by-unique-name' (same as the backend default).
const DC_SCRAPE_TYPES = {
    'by-unique-name': 'Unique Names',
    'by-bar-code': 'Advocate Bar Code',
    'both': 'Both',
};

const emptyBarCode = () => ({ stateCode: 'G', number: '', year: '' });

const emptyDistrictCourtDetail = () => ({
    state: '',
    stateCourtComplex: '',
    stateDistrict: '',
    'scrape-type': 'by-unique-name',
    barCodes: [],
});

const usesBarCodes = (detail) => detail['scrape-type'] === 'by-bar-code' || detail['scrape-type'] === 'both';

const AddEditCourtDetailsForm = () => {
    const { id } = useParams(); // company id
    const navigate = useNavigate();
    const { showLoading, hideLoading } = useLoading();

    const [uniqueNames, setUniqueNames] = useState(['']);
    const [highCourtDetails, setHighCourtDetails] = useState([{ highCourt: '', bench: '' }]);
    const [bombayHCDetails, setBombayHCDetails] = useState([]);
    const [gujaratHCDetails, setGujaratHCDetails] = useState([]);
    const { showToast } = useToast();
    const [districtCourtDetails, setDistrictCourtDetails] = useState([emptyDistrictCourtDetail()]);
    const [supremeCourtDetails, setSupremeCourtDetails] = useState([]);

    // Constants for Bombay HC
    const BOMBAY_BENCHES = { 1: "Bombay", 2: "Aurangabad", 3: "Nagpur", 4: "Goa", 7: "Kolhapur" };
    const JURISDICTIONS = { "C": "Civil", "CR": "Criminal", "OS": "Original" };
    const PETN_RESP = { "P": "Petitioner", "R": "Respondent" };
    const YEARS = Array.from({ length: 2026 - 1960 + 1 }, (_, i) => 2026 - i);

    // Constants for Supreme Court
    const SC_CASE_STATUS = ['Pending', 'Disposed'];
    const SC_PARTY_TYPES = { "P": "Petitioner", "R": "Respondent" };

    // Fetch existing data when component mounts
    useEffect(() => {
        if (id) {
            showLoading('Loading company data...');
        }

        const fetchCompanyData = async () => {
            try {
                const response = await getCompanyDataByID(id);
                const companyData = response.data;
                console.log(companyData, "wanted")

                // Set unique names if they exist
                if (companyData.company.uniqueNames && companyData.company.uniqueNames.length > 0) {
                    setUniqueNames(companyData.company.uniqueNames);
                } else {
                    setUniqueNames(['']); // Default empty field
                }

                // Process High Court Data (Split between Generic and Bombay HC)
                if (companyData.company.highCourt && companyData.company.highCourt.length > 0) {
                    const genericHC = [];
                    const bombayHC = [];

                    const gujaratHC = [];

                    companyData.company.highCourt.forEach(hc => {
                        if (hc['court-type'] === 'maharashtra-hc') {
                            bombayHC.push(hc);
                        } else if (hc['court-type'] === 'gujarat-hc') {
                            gujaratHC.push(hc);
                        } else if (hc.highCourt) {
                            genericHC.push({
                                highCourt: hc.highCourt.toString(),
                                bench: hc.bench ? hc.bench.toString() : ''
                            });
                        }
                    });

                    if (genericHC.length > 0) {
                        setHighCourtDetails(genericHC);
                    } else {
                        setHighCourtDetails([{ highCourt: '', bench: '' }]);
                    }

                    if (bombayHC.length > 0) {
                        setBombayHCDetails(bombayHC);
                    } else {
                        setBombayHCDetails([]);
                    }

                    if (gujaratHC.length > 0) {
                        setGujaratHCDetails(gujaratHC);
                    } else {
                        setGujaratHCDetails([]);
                    }
                } else {
                    setHighCourtDetails([{ highCourt: '', bench: '' }]);
                    setBombayHCDetails([]);
                }

                if (companyData.company.districtCourt && companyData.company.districtCourt.length > 0) {
                    const formattedDistrictCourtDetails = companyData.company.districtCourt.map(dc => ({
                        state: dc.state.toString(),
                        stateCourtComplex: dc.stateCourtComplex.toString(),
                        stateDistrict: dc.stateDistrict.toString(),
                        'scrape-type': dc['scrape-type'] || 'by-unique-name',
                        barCodes: (dc.barCodes || []).map(b => ({ stateCode: b.stateCode, number: b.number, year: b.year }))
                    }));
                    setDistrictCourtDetails(formattedDistrictCourtDetails);
                } else {
                    setDistrictCourtDetails([emptyDistrictCourtDetail()]);
                }

                if (companyData.company.supremeCourt && companyData.company.supremeCourt.length > 0) {
                    setSupremeCourtDetails(companyData.company.supremeCourt);
                } else {
                    setSupremeCourtDetails([]);
                }

                const hasExistingData = (companyData.company.uniqueNames && companyData.company.uniqueNames.length > 0) ||
                    (companyData.company.highCourt && companyData.company.highCourt.length > 0) ||
                    (companyData.company.districtCourt && companyData.company.districtCourt.length > 0);

                if (hasExistingData) {
                    showToast('Data loaded successfully!', 'success');
                }

            } catch (error) {
                console.error('Error fetching company data:', error);
                showToast('Error loading existing data. Please try again.', 'error');
            } finally {
                hideLoading();
            }
        };

        if (id) {
            fetchCompanyData();
        } else {
            hideLoading();
        }
    }, [id]);

    const addUniqueName = () => {
        setUniqueNames([...uniqueNames, '']);
    };

    const removeUniqueName = (index) => {
        if (uniqueNames.length > 1) {
            const updated = uniqueNames.filter((_, i) => i !== index);
            setUniqueNames(updated);
        }
    };

    const updateUniqueName = (index, value) => {
        const updated = [...uniqueNames];
        updated[index] = value;
        setUniqueNames(updated);
    };

    const addHighCourtDetail = () => {
        setHighCourtDetails([...highCourtDetails, { highCourt: '', bench: '' }]);
    };

    const removeHighCourtDetail = (index) => {
        if (highCourtDetails.length > 1) {
            const updated = highCourtDetails.filter((_, i) => i !== index);
            setHighCourtDetails(updated);
        }
    };

    const updateHighCourtDetail = (index, field, value) => {
        const updated = [...highCourtDetails];
        updated[index][field] = value;

        // Reset bench when high court changes
        if (field === 'highCourt') {
            updated[index].bench = '';
        }

        setHighCourtDetails(updated);
    };

    // --- Bombay HC Handlers ---
    const addBombayHCDetail = () => {
        setBombayHCDetails([...bombayHCDetails, {
            'scrape-type': 'by-advocate',
            bench: '',
            jurisdiction: '',
            petnResp: '',
            advocateName: '',
            partyName: '',
            filingYear: '',
            cnr: ''
        }]);
    };

    const removeBombayHCDetail = (index) => {
        const updated = bombayHCDetails.filter((_, i) => i !== index);
        setBombayHCDetails(updated);
    };

    const updateBombayHCDetail = (index, field, value) => {
        const updated = [...bombayHCDetails];
        updated[index][field] = value;
        setBombayHCDetails(updated);
    };
    // ---------------------------

    // --- Gujarat HC Handlers ---
    const addGujaratHCDetail = () => {
        setGujaratHCDetails([...gujaratHCDetails, {
            'scrape-type': 'by-advocate',
            advocateName: '',
            partyName: '',
            cnr: '',
            caseYearFrom: '',
            caseYearTo: ''
        }]);
    };

    const removeGujaratHCDetail = (index) => {
        const updated = gujaratHCDetails.filter((_, i) => i !== index);
        setGujaratHCDetails(updated);
    };

    const updateGujaratHCDetail = (index, field, value) => {
        const updated = [...gujaratHCDetails];
        updated[index][field] = value;
        setGujaratHCDetails(updated);
    };
    // ---------------------------

    // --- Supreme Court Handlers ---
    const addSupremeCourtDetail = () => {
        setSupremeCourtDetails([...supremeCourtDetails, {
            'scrape-type': 'by-advocate',
            advocateName: '',
            year: '',
            caseStatus: '',
            partyName: '',
            partyType: '',
            cnr: ''
        }]);
    };

    const removeSupremeCourtDetail = (index) => {
        const updated = supremeCourtDetails.filter((_, i) => i !== index);
        setSupremeCourtDetails(updated);
    };

    const updateSupremeCourtDetail = (index, field, value) => {
        const updated = [...supremeCourtDetails];
        updated[index][field] = value;
        setSupremeCourtDetails(updated);
    };
    // ------------------------------

    const addDistrictCourtDetail = () => {
        setDistrictCourtDetails([...districtCourtDetails, emptyDistrictCourtDetail()]);
    };

    const removeDistrictCourtDetail = (index) => {
        if (districtCourtDetails.length > 1) {
            const updated = districtCourtDetails.filter((_, i) => i !== index);
            setDistrictCourtDetails(updated);
        }
    };

    const updateDistrictCourtDetail = (index, field, value) => {
        const updated = [...districtCourtDetails];
        updated[index][field] = value;

        // Reset dependent fields when parent changes
        if (field === 'state') {
            updated[index].stateDistrict = '';
            updated[index].stateCourtComplex = '';
        } else if (field === 'stateDistrict') {
            updated[index].stateCourtComplex = '';
        }

        // First switch to a bar code scrape type starts with one empty bar code row
        if (field === 'scrape-type' && usesBarCodes(updated[index]) && updated[index].barCodes.length === 0) {
            updated[index].barCodes = [emptyBarCode()];
        }

        setDistrictCourtDetails(updated);
    };

    const updateDcBarCodes = (index, getBarCodes) => {
        setDistrictCourtDetails(districtCourtDetails.map((detail, i) =>
            i === index ? { ...detail, barCodes: getBarCodes(detail.barCodes) } : detail
        ));
    };

    const addDcBarCode = (index) => updateDcBarCodes(index, barCodes => [...barCodes, emptyBarCode()]);

    const removeDcBarCode = (index, barCodeIndex) =>
        updateDcBarCodes(index, barCodes => barCodes.filter((_, i) => i !== barCodeIndex));

    const updateDcBarCode = (index, barCodeIndex, field, value) =>
        updateDcBarCodes(index, barCodes => barCodes.map((barCode, i) =>
            i === barCodeIndex ? { ...barCode, [field]: value } : barCode
        ));

    const getAvailableBenches = (highCourtId) => {
        if (!highCourtId || !state[highCourtId]) return {};
        return state[highCourtId].benches;
    };

    const getAvailableDistricts = (stateId) => {
        if (!stateId || !districtCourtMapping.states[stateId]) {
            return {};
        }
        return districtCourtMapping.states[stateId].districts;
    };

    const getAvailableCourtComplexes = (stateId, districtId) => {
        if (!stateId || !districtId ||
            !districtCourtMapping.states[stateId] ||
            !districtCourtMapping.states[stateId].districts[districtId]) {
            return {};
        }
        return districtCourtMapping.states[stateId].districts[districtId].courtComplexes;
    };

    const validateForm = () => {
        // Check for empty unique names
        const filteredUniqueNames = uniqueNames.filter(name => name.trim());
        const selectedDistrictCourts = districtCourtDetails.filter(detail =>
            detail.state && detail.stateCourtComplex && detail.stateDistrict
        );
        const hasDcBarCodes = selectedDistrictCourts.some(detail => usesBarCodes(detail) && detail.barCodes.length > 0);

        if (filteredUniqueNames.length === 0 && !hasDcBarCodes) {
            showToast('Please add at least one unique name or district court bar code', 'error');
            return false;
        }

        for (const [index, detail] of selectedDistrictCourts.entries()) {
            if (detail['scrape-type'] !== 'by-bar-code' && filteredUniqueNames.length === 0) {
                showToast(`District Court Detail ${index + 1} scrapes by unique names, but no unique name is added`, 'error');
                return false;
            }
            if (!usesBarCodes(detail)) continue;

            if (detail.barCodes.length === 0) {
                showToast(`District Court Detail ${index + 1}: add at least one bar code`, 'error');
                return false;
            }
            const incomplete = detail.barCodes.some(b => !b.stateCode.trim() || !b.number.trim() || !/^\d{4}$/.test(b.year.trim()));
            if (incomplete) {
                showToast(`District Court Detail ${index + 1}: every bar code needs a state code, number and 4-digit year`, 'error');
                return false;
            }
        }

        // The scraper finds a district court entry by state + district + court complex, so each court can be added only once
        const courtKeys = selectedDistrictCourts.map(d => `${d.state}|${d.stateDistrict}|${d.stateCourtComplex}`);
        if (new Set(courtKeys).size !== courtKeys.length) {
            showToast('The same district court complex is added more than once. Use "Both" to scrape it by names and bar codes.', 'error');
            return false;
        }

        // Check for duplicate unique names
        const duplicateNames = filteredUniqueNames.filter((name, index) =>
            filteredUniqueNames.indexOf(name) !== index
        );
        if (duplicateNames.length > 0) {
            showToast('Duplicate unique names found. Please remove duplicates.', 'error');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        // Filter out empty values for Unique Names
        const filteredUniqueNames = uniqueNames.filter(name => name.trim());

        // Process Generic High Court Details
        const filteredHighCourtDetails = highCourtDetails.filter(detail =>
            detail.highCourt && detail.bench
        ).map(detail => ({
            highCourt: parseInt(detail.highCourt),
            bench: parseInt(detail.bench)
        }));

        // Process Bombay HC Details
        const processedBombayHCDetails = bombayHCDetails.map(item => {
            const base = {
                "court-type": "maharashtra-hc",
                "scrape-type": item['scrape-type']
            };

            if (item['scrape-type'] === 'by-advocate') {
                return {
                    ...base,
                    bench: item.bench ? parseInt(item.bench) : null,
                    jurisdiction: item.jurisdiction,
                    petnResp: item.petnResp,
                    advocateName: item.advocateName
                };
            } else if (item['scrape-type'] === 'by-party') {
                return {
                    ...base,
                    bench: item.bench ? parseInt(item.bench) : null,
                    jurisdiction: item.jurisdiction,
                    petnResp: item.petnResp,
                    partyName: item.partyName,
                    filingYear: item.filingYear
                };
            } else if (item['scrape-type'] === 'by-cnr') {
                return {
                    ...base,
                    cnr: item.cnr
                };
            }
            return null;
        }).filter(item => item !== null); // Basic filtering, you might want more validation

        const filteredDistrictCourtDetails = districtCourtDetails.filter(detail =>
            detail.state && detail.stateCourtComplex && detail.stateDistrict
        ).map(detail => ({
            state: detail.state,
            stateCourtComplex: detail.stateCourtComplex,
            stateDistrict: detail.stateDistrict,
            'scrape-type': detail['scrape-type'],
            barCodes: usesBarCodes(detail)
                ? detail.barCodes.map(b => ({
                    stateCode: b.stateCode.trim().toUpperCase(),
                    number: b.number.trim(),
                    year: b.year.trim()
                }))
                : []
        }));

        const processedSupremeCourtDetails = supremeCourtDetails.map(item => {
            const base = {
                'court-type': 'supreme-court',
                'scrape-type': item['scrape-type']
            };
            if (item['scrape-type'] === 'by-advocate') {
                return {
                    ...base,
                    advocateName: item.advocateName,
                    year: item.year,
                    caseStatus: item.caseStatus
                };
            } else if (item['scrape-type'] === 'by-party') {
                return {
                    ...base,
                    partyName: item.partyName,
                    partyType: item.partyType,
                    year: item.year,
                    caseStatus: item.caseStatus
                };
            } else if (item['scrape-type'] === 'by-cnr') {
                return { ...base, cnr: item.cnr };
            }
            return null;
        }).filter(item => item !== null);

        const processedGujaratHCDetails = gujaratHCDetails.map(item => {
            const base = {
                'court-type': 'gujarat-hc',
                'scrape-type': item['scrape-type']
            };
            if (item['scrape-type'] === 'by-advocate') {
                return { ...base, advocateName: item.advocateName, caseYearFrom: item.caseYearFrom, caseYearTo: item.caseYearTo };
            } else if (item['scrape-type'] === 'by-party') {
                return { ...base, partyName: item.partyName, caseYearFrom: item.caseYearFrom, caseYearTo: item.caseYearTo };
            } else if (item['scrape-type'] === 'by-cnr') {
                return { ...base, cnr: item.cnr };
            }
            return null;
        }).filter(item => item !== null);

        const payload = {
            uniqueNames: filteredUniqueNames,
            highCourt: [...filteredHighCourtDetails, ...processedBombayHCDetails, ...processedGujaratHCDetails],
            districtCourt: filteredDistrictCourtDetails,
            supremeCourt: processedSupremeCourtDetails
        };

        console.log('Payload:', payload);

        try {
            showLoading('Saving court details...');
            await companyaddeditcourtdetails(id, payload);
            hideLoading();
            showToast('Court details saved successfully!', 'success');
            setTimeout(() => {
                navigate('/home/superadmin');
            }, 2000);
        } catch (error) {
            hideLoading();
            console.error('Error saving court details:', error);
            showToast('Error saving court details. Please try again.', 'error');
        }
    };

    return (
        <div className="add-edit-court-details-container">
            <div className="add-edit-court-details-wrapper">
                <div className="add-edit-court-details-header">
                    <h2 className="add-edit-court-details-title">Add/Edit Court Details</h2>
                    <p className="add-edit-court-details-subtitle">Configure the court details for this company</p>
                </div>

                <hr className="add-edit-court-details-divider" />

                <form onSubmit={handleSubmit} className="form-content">
                    {/* Unique Names Section */}
                    <div className="form-section">
                        <h3>Unique Names</h3>
                        <div className="form-grid">
                            {uniqueNames.map((name, index) => (
                                <div key={index} className="court-det-form-group">
                                    <label htmlFor={`uniqueName-${index}`}>
                                        Unique Name {index + 1} <span className="required">*</span>
                                    </label>
                                    <div className="input-with-button">
                                        <input
                                            type="text"
                                            id={`uniqueName-${index}`}
                                            value={name}
                                            onChange={(e) => updateUniqueName(index, e.target.value)}
                                            placeholder="Enter unique name"
                                            className="text-input"
                                        />
                                        {uniqueNames.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeUniqueName(index)}
                                                className="remove-btn"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addUniqueName}
                            className="add-btn"
                        >
                            + Add Unique Name
                        </button>
                    </div>

                    {/* High Court Details Section */}
                    <div className="form-section">
                        <h3>High Court Details</h3>
                        <div className="form-grid">
                            {highCourtDetails.map((detail, index) => (
                                <div key={index} className="court-det-form-group full-width">
                                    <label>High Court Detail {index + 1}</label>
                                    <div className="high-court-inputs">
                                        <select
                                            value={detail.highCourt}
                                            onChange={(e) => updateHighCourtDetail(index, 'highCourt', e.target.value)}
                                            className="dropdown"
                                        >
                                            <option value="">Select High Court</option>
                                            {Object.entries(state).map(([key, value]) => (
                                                <option key={key} value={key}>
                                                    {value.highCourt}
                                                </option>
                                            ))}
                                        </select>

                                        <select
                                            value={detail.bench}
                                            onChange={(e) => updateHighCourtDetail(index, 'bench', e.target.value)}
                                            className="dropdown"
                                            disabled={!detail.highCourt}
                                        >
                                            <option value="">Select Bench</option>
                                            {detail.highCourt && Object.entries(getAvailableBenches(detail.highCourt)).map(([key, value]) => (
                                                <option key={key} value={key}>
                                                    {value}
                                                </option>
                                            ))}
                                        </select>

                                        {highCourtDetails.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeHighCourtDetail(index)}
                                                className="remove-btn"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addHighCourtDetail}
                            className="add-btn"
                        >
                            + Add High Court Detail
                        </button>
                    </div>

                    {/* District Court Details Section */}
                    <div className="form-section">
                        <h3>District Court Details</h3>
                        <div className="form-grid">
                            {districtCourtDetails.map((detail, index) => (
                                <div key={index} className="court-det-form-group full-width">
                                    <label>District Court Detail {index + 1}</label>
                                    <div className="district-court-inputs">
                                        <select
                                            value={detail.state}
                                            onChange={(e) => updateDistrictCourtDetail(index, 'state', e.target.value)}
                                            className="dropdown"
                                        >
                                            <option value="">Select State</option>
                                            {Object.entries(districtCourtMapping.states).map(([stateId, stateData]) => (
                                                <option key={stateId} value={stateId}>
                                                    {stateData.name}
                                                </option>
                                            ))}
                                        </select>

                                        <select
                                            value={detail.stateDistrict}
                                            onChange={(e) => updateDistrictCourtDetail(index, 'stateDistrict', e.target.value)}
                                            className="dropdown"
                                            disabled={!detail.state}
                                        >
                                            <option value="">Select District</option>
                                            {detail.state && Object.entries(getAvailableDistricts(detail.state)).map(([districtId, districtData]) => (
                                                <option key={districtId} value={districtId}>
                                                    {districtData.name}
                                                </option>
                                            ))}
                                        </select>

                                        <select
                                            value={detail.stateCourtComplex}
                                            onChange={(e) => updateDistrictCourtDetail(index, 'stateCourtComplex', e.target.value)}
                                            className="dropdown"
                                            disabled={!detail.state || !detail.stateDistrict}
                                        >
                                            <option value="">Select Court Complex</option>
                                            {detail.state && detail.stateDistrict &&
                                                Object.entries(getAvailableCourtComplexes(detail.state, detail.stateDistrict)).map(([complexId, complexData]) => (
                                                    <option key={complexId} value={complexId}>
                                                        {complexData.name}
                                                    </option>
                                                ))}
                                        </select>

                                        {districtCourtDetails.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeDistrictCourtDetail(index)}
                                                className="remove-btn"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>

                                    <div className="dc-scrape-options">
                                        <div className="dc-scrape-type">
                                            <span>Scrape by</span>
                                            <select
                                                value={detail['scrape-type']}
                                                onChange={(e) => updateDistrictCourtDetail(index, 'scrape-type', e.target.value)}
                                                className="dropdown"
                                            >
                                                {Object.entries(DC_SCRAPE_TYPES).map(([value, label]) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {usesBarCodes(detail) && (
                                            <>
                                                {detail.barCodes.map((barCode, barCodeIndex) => (
                                                    <div key={barCodeIndex} className="dc-bar-code-row">
                                                        <input
                                                            type="text"
                                                            value={barCode.stateCode}
                                                            onChange={(e) => updateDcBarCode(index, barCodeIndex, 'stateCode', e.target.value)}
                                                            placeholder="State (G)"
                                                            maxLength={6}
                                                            className="text-input"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={barCode.number}
                                                            onChange={(e) => updateDcBarCode(index, barCodeIndex, 'number', e.target.value)}
                                                            placeholder="Bar code number"
                                                            maxLength={8}
                                                            className="text-input"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={barCode.year}
                                                            onChange={(e) => updateDcBarCode(index, barCodeIndex, 'year', e.target.value)}
                                                            placeholder="Year"
                                                            maxLength={4}
                                                            className="text-input"
                                                        />
                                                        {detail.barCodes.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeDcBarCode(index, barCodeIndex)}
                                                                className="remove-btn"
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => addDcBarCode(index)}
                                                    className="add-btn"
                                                >
                                                    + Add Bar Code
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addDistrictCourtDetail}
                            className="add-btn"
                        >
                            + Add District Court Detail
                        </button>
                    </div>

                </form>
            </div>

            {/* Bombay High Court Details Section - Separate Card */}
            <div className="bombay-hc-section-wrapper">
                <h3 className="bombay-hc-title">Bombay High Court Scraper Details</h3>
                
                <div className="bombay-hc-content">
                    <div className="bombay-hc-grid">
                        {bombayHCDetails.map((detail, index) => (
                            <div key={index} className="bombay-hc-card">
                                <div className="bombay-hc-card-header">
                                    <label>Detail {index + 1}</label>
                                    <button
                                        type="button"
                                        onClick={() => removeBombayHCDetail(index)}
                                        className="remove-btn"
                                    >
                                        ×
                                    </button>
                                </div>

                                {/* Scrape Type Selection */}
                                <div className="bombay-hc-field-group">
                                    <label>Scrape Type</label>
                                    <select
                                        value={detail['scrape-type']}
                                        onChange={(e) => updateBombayHCDetail(index, 'scrape-type', e.target.value)}
                                        className="dropdown"
                                    >
                                        <option value="by-advocate">Advocate</option>
                                        <option value="by-party">Party</option>
                                        <option value="by-cnr">CNR</option>
                                    </select>
                                </div>

                                {/* Fields for Advocate and Party (Shared Fields) */}
                                {(detail['scrape-type'] === 'by-advocate' || detail['scrape-type'] === 'by-party') && (
                                    <div className="bombay-hc-fields-grid">
                                        <div className="bombay-hc-field">
                                            <label>Bench</label>
                                            <select
                                                value={detail.bench}
                                                onChange={(e) => updateBombayHCDetail(index, 'bench', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Bench</option>
                                                {Object.entries(BOMBAY_BENCHES).map(([key, value]) => (
                                                    <option key={key} value={key}>{value}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Jurisdiction</label>
                                            <select
                                                value={detail.jurisdiction}
                                                onChange={(e) => updateBombayHCDetail(index, 'jurisdiction', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Jurisdiction</option>
                                                {Object.entries(JURISDICTIONS).map(([key, value]) => (
                                                    <option key={key} value={key}>{value}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Petitioner/Respondent</label>
                                            <select
                                                value={detail.petnResp}
                                                onChange={(e) => updateBombayHCDetail(index, 'petnResp', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Type</option>
                                                {Object.entries(PETN_RESP).map(([key, value]) => (
                                                    <option key={key} value={key}>{value}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Specific Field for Advocate */}
                                        {detail['scrape-type'] === 'by-advocate' && (
                                            <div className="bombay-hc-field">
                                                <label>Advocate Name</label>
                                                <input
                                                    type="text"
                                                    value={detail.advocateName || ''}
                                                    onChange={(e) => updateBombayHCDetail(index, 'advocateName', e.target.value)}
                                                    placeholder="Enter Advocate Name"
                                                    className="text-input"
                                                />
                                            </div>
                                        )}
                                        {/* Specific Fields for Party */}
                                        {detail['scrape-type'] === 'by-party' && (
                                            <>
                                                <div className="bombay-hc-field">
                                                    <label>Party Name</label>
                                                    <input
                                                        type="text"
                                                        value={detail.partyName || ''}
                                                        onChange={(e) => updateBombayHCDetail(index, 'partyName', e.target.value)}
                                                        placeholder="Enter Party Name"
                                                        className="text-input"
                                                    />
                                                </div>
                                                <div className="bombay-hc-field">
                                                    <label>Filing Year</label>
                                                    <select
                                                        value={detail.filingYear}
                                                        onChange={(e) => updateBombayHCDetail(index, 'filingYear', e.target.value)}
                                                        className="dropdown"
                                                    >
                                                        <option value="">Select Year</option>
                                                        {YEARS.map(year => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Fields for CNR */}
                                {detail['scrape-type'] === 'by-cnr' && (
                                    <div className="bombay-hc-cnr-field">
                                        <label>CNR Number</label>
                                        <input
                                            type="text"
                                            value={detail.cnr || ''}
                                            onChange={(e) => updateBombayHCDetail(index, 'cnr', e.target.value)}
                                            placeholder="Enter CNR Number"
                                            className="text-input"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    
                    <button
                        type="button"
                        onClick={addBombayHCDetail}
                        className="add-btn"
                    >
                        + Add Bombay HC Detail
                    </button>
                </div>
            </div>

            {/* Gujarat High Court Scraper Details Section */}
            <div className="bombay-hc-section-wrapper">
                <h3 className="bombay-hc-title">Gujarat High Court Scraper Details</h3>

                <div className="bombay-hc-content">
                    <div className="bombay-hc-grid">
                        {gujaratHCDetails.map((detail, index) => (
                            <div key={index} className="bombay-hc-card">
                                <div className="bombay-hc-card-header">
                                    <label>Detail {index + 1}</label>
                                    <button
                                        type="button"
                                        onClick={() => removeGujaratHCDetail(index)}
                                        className="remove-btn"
                                    >
                                        ×
                                    </button>
                                </div>

                                {/* Scrape Type */}
                                <div className="bombay-hc-field-group">
                                    <label>Scrape Type</label>
                                    <select
                                        value={detail['scrape-type']}
                                        onChange={(e) => updateGujaratHCDetail(index, 'scrape-type', e.target.value)}
                                        className="dropdown"
                                    >
                                        <option value="by-advocate">Advocate</option>
                                        <option value="by-party">Party</option>
                                        <option value="by-cnr">CNR</option>
                                    </select>
                                </div>

                                {/* by-advocate fields */}
                                {detail['scrape-type'] === 'by-advocate' && (
                                    <div className="bombay-hc-fields-grid">
                                        <div className="bombay-hc-field">
                                            <label>Advocate Name</label>
                                            <input
                                                type="text"
                                                value={detail.advocateName || ''}
                                                onChange={(e) => updateGujaratHCDetail(index, 'advocateName', e.target.value)}
                                                placeholder="Enter Advocate Name"
                                                className="text-input"
                                            />
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Case Year From</label>
                                            <select
                                                value={detail.caseYearFrom || ''}
                                                onChange={(e) => updateGujaratHCDetail(index, 'caseYearFrom', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Year</option>
                                                {YEARS.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Case Year To</label>
                                            <select
                                                value={detail.caseYearTo || ''}
                                                onChange={(e) => updateGujaratHCDetail(index, 'caseYearTo', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Year</option>
                                                {YEARS.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* by-party fields */}
                                {detail['scrape-type'] === 'by-party' && (
                                    <div className="bombay-hc-fields-grid">
                                        <div className="bombay-hc-field">
                                            <label>Party Name</label>
                                            <input
                                                type="text"
                                                value={detail.partyName || ''}
                                                onChange={(e) => updateGujaratHCDetail(index, 'partyName', e.target.value)}
                                                placeholder="Enter Party Name"
                                                className="text-input"
                                            />
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Case Year From</label>
                                            <select
                                                value={detail.caseYearFrom || ''}
                                                onChange={(e) => updateGujaratHCDetail(index, 'caseYearFrom', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Year</option>
                                                {YEARS.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Case Year To</label>
                                            <select
                                                value={detail.caseYearTo || ''}
                                                onChange={(e) => updateGujaratHCDetail(index, 'caseYearTo', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Year</option>
                                                {YEARS.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* by-cnr fields */}
                                {detail['scrape-type'] === 'by-cnr' && (
                                    <div className="bombay-hc-cnr-field">
                                        <label>CNR Number</label>
                                        <input
                                            type="text"
                                            value={detail.cnr || ''}
                                            onChange={(e) => updateGujaratHCDetail(index, 'cnr', e.target.value)}
                                            placeholder="Enter CNR Number"
                                            className="text-input"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={addGujaratHCDetail}
                        className="add-btn"
                    >
                        + Add Gujarat HC Detail
                    </button>
                </div>
            </div>

            {/* Supreme Court Scraper Details Section */}
            <div className="bombay-hc-section-wrapper">
                <h3 className="bombay-hc-title">Supreme Court Scraper Details</h3>

                <div className="bombay-hc-content">
                    <div className="bombay-hc-grid">
                        {supremeCourtDetails.map((detail, index) => (
                            <div key={index} className="bombay-hc-card">
                                <div className="bombay-hc-card-header">
                                    <label>Detail {index + 1}</label>
                                    <button
                                        type="button"
                                        onClick={() => removeSupremeCourtDetail(index)}
                                        className="remove-btn"
                                    >
                                        ×
                                    </button>
                                </div>

                                {/* Scrape Type */}
                                <div className="bombay-hc-field-group">
                                    <label>Scrape Type</label>
                                    <select
                                        value={detail['scrape-type']}
                                        onChange={(e) => updateSupremeCourtDetail(index, 'scrape-type', e.target.value)}
                                        className="dropdown"
                                    >
                                        <option value="by-advocate">Advocate</option>
                                        <option value="by-party">Party</option>
                                        <option value="by-cnr">CNR</option>
                                    </select>
                                </div>

                                {/* by-advocate fields */}
                                {detail['scrape-type'] === 'by-advocate' && (
                                    <div className="bombay-hc-fields-grid">
                                        <div className="bombay-hc-field">
                                            <label>Advocate Name</label>
                                            <input
                                                type="text"
                                                value={detail.advocateName || ''}
                                                onChange={(e) => updateSupremeCourtDetail(index, 'advocateName', e.target.value)}
                                                placeholder="Enter Advocate Name"
                                                className="text-input"
                                            />
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Year</label>
                                            <select
                                                value={detail.year || ''}
                                                onChange={(e) => updateSupremeCourtDetail(index, 'year', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Year</option>
                                                {YEARS.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Case Status</label>
                                            <select
                                                value={detail.caseStatus || ''}
                                                onChange={(e) => updateSupremeCourtDetail(index, 'caseStatus', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Status</option>
                                                {SC_CASE_STATUS.map(s => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* by-party fields */}
                                {detail['scrape-type'] === 'by-party' && (
                                    <div className="bombay-hc-fields-grid">
                                        <div className="bombay-hc-field">
                                            <label>Party Name</label>
                                            <input
                                                type="text"
                                                value={detail.partyName || ''}
                                                onChange={(e) => updateSupremeCourtDetail(index, 'partyName', e.target.value)}
                                                placeholder="Enter Party Name"
                                                className="text-input"
                                            />
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Party Type</label>
                                            <select
                                                value={detail.partyType || ''}
                                                onChange={(e) => updateSupremeCourtDetail(index, 'partyType', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Type</option>
                                                {Object.entries(SC_PARTY_TYPES).map(([key, value]) => (
                                                    <option key={key} value={key}>{value}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Year</label>
                                            <select
                                                value={detail.year || ''}
                                                onChange={(e) => updateSupremeCourtDetail(index, 'year', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Year</option>
                                                {YEARS.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="bombay-hc-field">
                                            <label>Case Status</label>
                                            <select
                                                value={detail.caseStatus || ''}
                                                onChange={(e) => updateSupremeCourtDetail(index, 'caseStatus', e.target.value)}
                                                className="dropdown"
                                            >
                                                <option value="">Select Status</option>
                                                {SC_CASE_STATUS.map(s => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* by-cnr fields */}
                                {detail['scrape-type'] === 'by-cnr' && (
                                    <div className="bombay-hc-cnr-field">
                                        <label>CNR Number</label>
                                        <input
                                            type="text"
                                            value={detail.cnr || ''}
                                            onChange={(e) => updateSupremeCourtDetail(index, 'cnr', e.target.value)}
                                            placeholder="Enter CNR Number"
                                            className="text-input"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={addSupremeCourtDetail}
                        className="add-btn"
                    >
                        + Add Supreme Court Detail
                    </button>
                </div>
            </div>

            {/* Form Actions - Separate Section at Bottom */}
            <div className="form-actions-wrapper">
                <button
                    type="button"
                    onClick={() => navigate('/home/superadmin')}
                    className="btn-cancel"
                >
                    Cancel
                </button>
                <button 
                    type="button" 
                    onClick={handleSubmit}
                    className="btn-submit"
                >
                    Save Court Details
                </button>
            </div>
        </div>
    );
};

export default AddEditCourtDetailsForm;